import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationContext as useOrganization } from '../contexts/OrganizationContext';
import { UserMenu } from './UserMenu';
import {
  LayoutDashboard,
  Briefcase,
  UserCircle,
  ListTodo,
  MessageSquare,
  Trophy,
  Zap,
  TrendingUp,
  ChevronDown,
  Building,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Coffee,
  Shield,
  Target,
  Search
} from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Logo } from './Logo';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { useTour } from '../hooks/useTour';
import { useRuns } from '../hooks/useRuns';

export function DashboardLayout() {
  const { logout } = useAuth();
  const { currentOrg, organizations, switchOrganization } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useTour();

  // Count runs pending approval for the sidebar badge
  const { runs: pendingRuns } = useRuns({ status: 'pending_approval', pageSize: 9999 });
  const pendingApprovalCount = pendingRuns.length;

  const _handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigationItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: Zap, label: 'Runs', path: '/runs', tourId: 'runs' },
    { icon: TrendingUp, label: 'Pipeline', path: '/pipeline', tourId: 'pipeline' },
    { icon: Briefcase, label: 'Jobs', path: '/jobs', tourId: 'jobs' },
    { icon: UserCircle, label: 'Contacts', path: '/contacts', tourId: 'contacts' },
    { icon: ListTodo, label: 'Tasks', path: '/tasks', tourId: 'tasks' },
    { icon: MessageSquare, label: 'Messages', path: '/messages', tourId: 'messages' },
  ];

  const teamItems = [
    { icon: Trophy, label: 'Team', path: '/team', tourId: 'team-tools' },
    { icon: BarChart3, label: 'Performance', path: '/team-performance' },
    { icon: Coffee, label: 'Daily Standup', path: '/daily-standup' },
    { icon: Target, label: 'Target Accounts', path: '/target-accounts' },
    { icon: Shield, label: 'Blacklist', path: '/blacklist' },
  ];

  // On mobile, always show expanded view
  const showExpanded = !isCollapsed || isMobileMenuOpen;

  return (
    <div className="min-h-screen flex bg-background relative">
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 right-4 z-50 px-3 py-2 bg-popover border border-border rounded-lg shadow-lg flex items-center gap-2"
        aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isMobileMenuOpen}
        aria-controls="main-navigation"
      >
        {isMobileMenuOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
        <span className="text-sm font-medium">{isMobileMenuOpen ? 'Close' : 'Menu'}</span>
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Navigation */}
      <nav
        id="main-navigation"
        aria-label="Main navigation"
        role="navigation"
        className={`
          ${isCollapsed ? 'w-16' : 'w-52'} 
          glass-card border-r border-sidebar-border flex flex-col sticky top-0 h-screen relative z-40 
          transition-all duration-300
          max-lg:fixed max-lg:!w-72 max-lg:top-20 max-lg:right-4 max-lg:bottom-4 max-lg:h-auto max-lg:max-h-[calc(100vh-7rem)] max-lg:rounded-xl max-lg:shadow-2xl
          ${isMobileMenuOpen ? 'max-lg:translate-x-0' : 'max-lg:translate-x-[calc(100%+2rem)]'}
        `}
      >
        {/* Logo */}
        <div className={`p-4 border-b border-sidebar-border ${isCollapsed && !isMobileMenuOpen ? 'px-3' : ''}`}>
          <Logo size="md" showText={showExpanded} />
        </div>

        {/* Organization Selector */}
        {showExpanded && currentOrg && (
          <div className="px-3 py-2 border-b border-sidebar-border">
            <div className="relative">
              <button
                onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-sidebar-hover transition-colors"
                aria-label="Select organization"
                aria-expanded={showOrgDropdown}
                aria-haspopup="menu"
                aria-controls="organization-menu"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Building className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  <span className="text-sm truncate">{currentOrg.name}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${showOrgDropdown ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              {showOrgDropdown && (
                <div 
                  id="organization-menu"
                  role="menu"
                  aria-label="Organization list"
                  className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden"
                >
                  {organizations.map((org) => (
                    <button
                      key={org.id}
                      role="menuitem"
                      onClick={() => {
                        switchOrganization(org.id);
                        setShowOrgDropdown(false);
                      }}
                      aria-current={currentOrg.id === org.id ? 'true' : undefined}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-sidebar-hover transition-colors ${
                        currentOrg.id === org.id ? 'bg-sidebar-hover' : ''
                      }`}
                    >
                      {org.name}
                      {currentOrg.id === org.id && (
                        <span className="sr-only"> (current organization)</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Collapsed Organization Indicator - Desktop only */}
        {isCollapsed && !isMobileMenuOpen && currentOrg && (
          <div className="px-2 py-3 border-b border-sidebar-border flex justify-center max-lg:hidden">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building className="w-4 h-4 text-primary" />
            </div>
          </div>
        )}

        {/* Navigation */}
        <TooltipProvider delayDuration={0}>
          {/* Global Search Trigger */}
          <div className={`px-3 pt-3 ${isCollapsed && !isMobileMenuOpen ? 'flex justify-center px-2' : ''}`}>
            {isCollapsed && !isMobileMenuOpen ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => window.dispatchEvent(new Event('open-global-search'))}
                    className="flex items-center justify-center w-10 h-10 rounded-lg text-sidebar-foreground hover:bg-sidebar-hover transition-colors"
                    aria-label="Search (Cmd+K)"
                  >
                    <Search className="w-4 h-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-medium">
                  Search <span className="text-muted-foreground">⌘K</span>
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={() => window.dispatchEvent(new Event('open-global-search'))}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-muted-foreground border border-sidebar-border hover:bg-sidebar-hover hover:text-sidebar-foreground transition-colors"
              >
                <Search className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left">Search</span>
                <span className="text-xs border border-border rounded px-1.5 py-0.5 flex-shrink-0">⌘K</span>
              </button>
            )}
          </div>

          <div className="flex-1 p-4 pt-3 space-y-3 overflow-y-auto" role="list">
            {/* Workspace Section */}
            <div>
              {showExpanded && (
                <h3 className="px-2.5 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Workspace
                </h3>
              )}
              <div className="space-y-1 mt-1" role="list">
                {navigationItems.map((item) => {
                  const showBadge = item.path === '/runs' && pendingApprovalCount > 0;
                  const navContent = (isActive: boolean) => (
                    <motion.div
                      className={`relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-gradient-to-r from-primary/15 via-primary/8 to-transparent text-sidebar-accent-foreground shadow-[inset_2px_0_0_0_var(--primary)]'
                          : 'text-sidebar-foreground hover:bg-sidebar-hover'
                      } ${isCollapsed && !isMobileMenuOpen ? 'justify-center' : ''}`}
                      whileHover={isCollapsed && !isMobileMenuOpen ? { scale: 1.15 } : {}}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                      {showExpanded && <span className="text-sm">{item.label}</span>}
                      {showExpanded && showBadge && (
                        <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 text-[11px] font-semibold text-white px-1.5">
                          {pendingApprovalCount}
                        </span>
                      )}
                      {!showExpanded && showBadge && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-white">
                          {pendingApprovalCount > 9 ? '9+' : pendingApprovalCount}
                        </span>
                      )}
                      {isActive && <span className="sr-only"> (current page)</span>}
                    </motion.div>
                  );

                  return isCollapsed && !isMobileMenuOpen ? (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={item.path}
                          end={item.path === '/'}
                          onClick={() => setIsMobileMenuOpen(false)}
                          aria-label={item.label}
                          data-tour={item.tourId}
                        >
                          {({ isActive }) => (
                            <div role="listitem" aria-current={isActive ? 'page' : undefined}>
                              {navContent(isActive)}
                            </div>
                          )}
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="font-medium">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/'}
                      onClick={() => setIsMobileMenuOpen(false)}
                      aria-label={item.label}
                      data-tour={item.tourId}
                    >
                      {({ isActive }) => (
                        <div role="listitem" aria-current={isActive ? 'page' : undefined}>
                          {navContent(isActive)}
                        </div>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>

            {/* Team Tools Section */}
            <div>
              {showExpanded && (
                <h3 className="px-2.5 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Team Tools
                </h3>
              )}
              <div className="space-y-1 mt-1" role="list">
                {teamItems.map((item) => {
                  const navContent = (isActive: boolean) => (
                    <motion.div
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-gradient-to-r from-primary/15 via-primary/8 to-transparent text-sidebar-accent-foreground shadow-[inset_2px_0_0_0_var(--primary)]'
                          : 'text-sidebar-foreground hover:bg-sidebar-hover'
                      } ${isCollapsed && !isMobileMenuOpen ? 'justify-center' : ''}`}
                      whileHover={isCollapsed && !isMobileMenuOpen ? { scale: 1.15 } : {}}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                      {showExpanded && <span className="text-sm">{item.label}</span>}
                      {isActive && <span className="sr-only"> (current page)</span>}
                    </motion.div>
                  );

                  return isCollapsed && !isMobileMenuOpen ? (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          aria-label={item.label}
                          data-tour={item.tourId}
                        >
                          {({ isActive }) => (
                            <div role="listitem" aria-current={isActive ? 'page' : undefined}>
                              {navContent(isActive)}
                            </div>
                          )}
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="font-medium">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      aria-label={item.label}
                      data-tour={item.tourId}
                    >
                      {({ isActive }) => (
                        <div role="listitem" aria-current={isActive ? 'page' : undefined}>
                          {navContent(isActive)}
                        </div>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>

          </div>
        </TooltipProvider>

        {/* User section */}
        <div className={`p-4 border-t border-sidebar-border ${isCollapsed && !isMobileMenuOpen ? 'px-2' : ''}`}>
          {showExpanded ? (
            <UserMenu showName={true} />
          ) : (
            <div className="flex justify-center">
              <UserMenu showName={false} />
            </div>
          )}
        </div>

        {/* Collapse/Expand Button - Desktop only */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex items-center justify-center absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-popover border border-border rounded-full shadow-lg hover:bg-sidebar-hover transition-colors"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!isCollapsed}
          aria-controls="main-navigation"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" aria-hidden="true" /> : <ChevronLeft className="w-3 h-3" aria-hidden="true" />}
        </button>
      </nav>

      {/* Main content */}
      <main id="main-content" role="main" aria-label="Page content" className="flex-1 relative z-10 overflow-y-auto overflow-x-hidden">
        {/* Page load transition — the one defined "page load" motion moment, see docs/DESIGN_SYSTEM.md */}
        <AnimatePresence>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

    </div>
  );
}